/**
 * Pure geo helpers for the asset-locations map widget.
 * Deliberately free of Leaflet / maplibre imports so Jest can load them.
 */

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

export interface MapBounds {
  northEast: LatLngLiteral;
  southWest: LatLngLiteral;
}

/** Leaflet's Projection.SphericalMercator.MAX_LATITUDE (edge of the Web-Mercator world). */
export const WORLD_MAX_LATITUDE = 85.0511287798;

/**
 * Leaflet `maxBounds` ([[south, west], [north, east]]): vertically the Web-Mercator world,
 * horizontally two world copies on each side. Panning across the antimeridian stays free
 * (continent-centred overviews need it, markers are repeated on the copies), only the
 * blank bands above/below the world are kept out of view.
 */
export const MAP_PAN_BOUNDS: [[number, number], [number, number]] = [
  [-WORLD_MAX_LATITUDE, -720],
  [WORLD_MAX_LATITUDE, 720],
];

/** Leaflet tile size: the world is `TILE_SIZE * 2^zoom` px wide and tall. */
const TILE_SIZE = 256;

/**
 * Zoom at which the Web-Mercator world is exactly `containerHeightPx` tall, i.e. the
 * smallest zoom without a blank band above/below the map. Null for a container without height.
 */
export const getFillZoom = (containerHeightPx: number): number | null =>
  containerHeightPx > 0 ? Math.log2(containerHeightPx / TILE_SIZE) : null;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** Wraps a longitude into [-180, 180). */
const wrapLng = (lng: number): number => ((((lng + 180) % 360) + 360) % 360) - 180;

/**
 * Turn a viewport bbox into valid query coordinates. A view wider than the world (fill
 * zoom on wide containers) becomes exactly -180..180; a view on a world copy is shifted
 * back; a view across the antimeridian becomes a box with west > east. The search
 * backend (Elasticsearch geo_bounding_box) accepts those, but picks a coarser cluster
 * precision for them than for a box of the same size that does not cross.
 */
export const normalizeQueryBounds = (bounds: MapBounds): MapBounds => {
  const span = bounds.northEast.lng - bounds.southWest.lng;
  const west = span >= 360 ? -180 : wrapLng(bounds.southWest.lng);
  let east = span >= 360 ? 180 : west + span;
  if (east > 180) east -= 360;
  return {
    southWest: { lat: clamp(bounds.southWest.lat, -90, 90), lng: west },
    northEast: { lat: clamp(bounds.northEast.lat, -90, 90), lng: east },
  };
};

/** Continent the world overview is centred on. */
export type MapHomeRegion = "americas" | "europeAfrica" | "asiaPacific";

const AMERICAS_TIME_ZONE_PREFIXES = ["America/", "US/", "Canada/", "Brazil/", "Chile/", "Mexico/"];
const ASIA_PACIFIC_TIME_ZONE_PREFIXES = ["Asia/", "Australia/", "Pacific/", "NZ"];

/** Home continent from an IANA time zone; anything unknown is centred on Europe/Africa. */
export const getMapHomeRegion = (timeZone: string | null | undefined): MapHomeRegion => {
  if (!timeZone) return "europeAfrica";
  if (AMERICAS_TIME_ZONE_PREFIXES.some((prefix) => timeZone.startsWith(prefix))) return "americas";
  if (ASIA_PACIFIC_TIME_ZONE_PREFIXES.some((prefix) => timeZone.startsWith(prefix))) return "asiaPacific";
  return "europeAfrica";
};

const OVERVIEW_SOUTH = -47; // southern tip of New Zealand / Tasmania — no Antarctica
const OVERVIEW_NORTH = 70; // North Cape, northern Alaska

/**
 * Initial view for a world-wide installed base, per home continent: the populated world
 * without Antarctica and without remote outliers (Hawaii, Tahiti), so the big continents
 * fill the widget. Longitudes beyond ±180 continue onto the neighbouring world copy.
 */
export const WORLD_OVERVIEW_BOUNDS: Record<MapHomeRegion, MapBounds> = {
  // US west coast … Japan / eastern Australia
  europeAfrica: {
    southWest: { lat: OVERVIEW_SOUTH, lng: -128 },
    northEast: { lat: OVERVIEW_NORTH, lng: 155 },
  },
  // East Asia (left, across the antimeridian) … Europe, Africa, Middle East
  americas: {
    southWest: { lat: OVERVIEW_SOUTH, lng: -240 },
    northEast: { lat: OVERVIEW_NORTH, lng: 45 },
  },
  // Europe … New Zealand, US west coast (right, across the antimeridian)
  asiaPacific: {
    southWest: { lat: OVERVIEW_SOUTH, lng: -15 },
    northEast: { lat: OVERVIEW_NORTH, lng: 235 },
  },
};

/** Asset bboxes at least this wide (degrees longitude) get the world overview instead. */
export const WORLDWIDE_MIN_LNG_SPAN = 180;

/**
 * Bounds the map fits initially: the asset bbox, unless the assets span the world (global
 * admins) — fitting that shows the whole globe at the smallest zoom, so the home
 * continent's overview is used instead.
 */
export const getInitialFitBounds = (assetBounds: MapBounds, homeRegion: MapHomeRegion): MapBounds =>
  assetBounds.northEast.lng - assetBounds.southWest.lng >= WORLDWIDE_MIN_LNG_SPAN
    ? WORLD_OVERVIEW_BOUNDS[homeRegion]
    : assetBounds;

/**
 * Longitude shifts (multiples of 360) of every world copy that intersects the viewport,
 * so markers can be repeated on the basemap's horizontal world copies. Always contains
 * at least one entry.
 */
export const worldCopyShifts = (viewWest: number, viewEast: number): number[] => {
  const shifts: number[] = [];
  for (let k = -3; k <= 3; k++) {
    const copyWest = -180 + 360 * k;
    const copyEast = 180 + 360 * k;
    if (copyEast > viewWest && copyWest < viewEast) {
      shifts.push(360 * k);
    }
  }
  return shifts.length > 0 ? shifts : [0];
};
